export type PublicationTheme = {
  accent: string;
  surface: string;
  glow: string;
};

export type PublicationSection = {
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

export type CaseStudy = {
  slug: string;
  client: string;
  category: string;
  title: string;
  deck: string;
  summary: string;
  timeline: string;
  services: string[];
  shareTitle: string;
  theme: PublicationTheme;
  stats: Array<{ label: string; value: string }>;
  sections: PublicationSection[];
};

export const caseStudies: CaseStudy[] = [
  {
    slug: 'golden-rule-dairy',
    client: 'Golden Rule Dairy',
    category: 'Case Study',
    title: 'Golden Rule Dairy',
    deck:
      'Repositioning a local dairy operation with a cleaner digital storefront, clearer offer architecture, and a stronger content foundation for growth.',
    summary:
      'Golden Rule Dairy needed a sharper digital identity and a serviceable website that could support trust, retail expansion, and repeat ordering. The work focused on simplifying the experience without flattening the story.',
    timeline: '12-week rollout',
    services: ['Brand positioning', 'Website design', 'Content structure', 'Lifecycle assets'],
    shareTitle: 'Golden Rule Dairy case study',
    theme: {
      accent: '#F8C968',
      surface: 'linear-gradient(135deg, rgba(45,29,9,0.96), rgba(85,58,20,0.88))',
      glow: 'rgba(248, 201, 104, 0.24)',
    },
    stats: [
      { label: 'Primary outcome', value: 'Sharper retail story' },
      { label: 'Key system shift', value: 'Simplified site architecture' },
      { label: 'Execution window', value: '12 weeks' },
    ],
    sections: [
      {
        title: 'Challenge',
        paragraphs: [
          'Golden Rule Dairy had a product story worth telling, but the digital presentation felt fragmented. Navigation, messaging, and visual hierarchy did not work together, which made the business appear smaller and less coherent than it was.',
          'The immediate challenge was not adding more content. It was restructuring the experience so customers and partners could understand the value faster.',
        ],
      },
      {
        title: 'Approach',
        paragraphs: [
          'We treated the brand and website as one system. The visual language was tightened, offer paths were simplified, and the content hierarchy was rebuilt around the decisions people actually needed to make.',
          'That created a cleaner storefront with more confidence, better pacing, and more room for the company story to carry weight without clutter.',
        ],
        bullets: [
          'Reframed homepage around trust and offer clarity',
          'Simplified navigation for retail and direct customers',
          'Built reusable content structure for future campaigns',
        ],
      },
      {
        title: 'Result',
        paragraphs: [
          'The final system gave Golden Rule Dairy a more credible and serviceable digital presence. Internally, the business also gained a cleaner platform for future campaigns and promotions instead of a site that needed constant workarounds.',
          'The project proved that clarity, sequencing, and visual control can be as valuable as raw feature count.',
        ],
      },
    ],
  },
  {
    slug: 'roshi-health',
    client: 'Roshi Health',
    category: 'Case Study',
    title: 'Roshi Health',
    deck:
      'Building a stronger health brand presence with disciplined design, clearer service framing, and a digital system that could evolve with the practice.',
    summary:
      'Roshi Health needed a digital identity and operating layer that felt trustworthy, modern, and easier to scale. The work focused on coherence first so every future touchpoint could inherit the same standard.',
    timeline: 'Ongoing engagement',
    services: ['Brand identity', 'Business organization setup', 'Website systems', 'Growth assets'],
    shareTitle: 'Roshi Health case study',
    theme: {
      accent: '#8BE0C9',
      surface: 'linear-gradient(135deg, rgba(9,34,31,0.96), rgba(13,63,55,0.9))',
      glow: 'rgba(139, 224, 201, 0.22)',
    },
    stats: [
      { label: 'Primary outcome', value: 'More credible brand system' },
      { label: 'Engagement type', value: 'Ongoing strategic support' },
      { label: 'Focus', value: 'Trust and scalability' },
    ],
    sections: [
      {
        title: 'Challenge',
        paragraphs: [
          'Healthcare brands operate under a higher trust threshold. Roshi Health needed a system that felt credible and calm while still carrying enough distinction to be memorable.',
          'The problem was not just visual polish. It was the absence of a unified digital identity that could support future growth.',
        ],
      },
      {
        title: 'Approach',
        paragraphs: [
          'We started by tightening the identity and operating structure so the business could present itself more clearly across its public and internal surfaces. That work then informed website direction, content rhythm, and future rollout decisions.',
          'The strategy favored durable systems over quick cosmetic fixes.',
        ],
        bullets: [
          'Clarified the public-facing identity system',
          'Improved structural consistency across digital touchpoints',
          'Created a cleaner base for future campaigns and service expansion',
        ],
      },
      {
        title: 'Result',
        paragraphs: [
          'Roshi Health now has a more coherent brand presence and a stronger digital foundation to build on. The engagement demonstrates how service design, identity discipline, and operational clarity can reinforce one another when treated as one system.',
          'That coherence is what makes future growth less expensive and less chaotic.',
        ],
      },
    ],
  },
];

export function getCaseStudyBySlug(slug?: string) {
  return caseStudies.find((study) => study.slug === slug);
}
